import * as React from 'react'

import { observer } from 'mobx-react'

import {
  ICardListGroupConfig,
  useCardListCore,
} from '~/client/src/shared/components/CardList'
import {
  GroupHeader,
  GroupHeaderTrigger,
  GroupIndent,
} from '~/client/src/shared/components/GroupHeader'
import { kindsOfIcon } from '~/client/src/shared/resources/domain/locationKind'
import type { IHierarchyAccessors } from '~/client/src/shared/utils/hierarchyAccessors'

import { iconLabel } from './iconCatalog'
import {
  iconGroupLabel,
  iconGroupNesting,
  iconGroupOf,
  typeLabel,
} from './iconGroups'
import IconPickerGrid from './IconPickerGrid'
import IconTile from './IconPickerIconTile'
import IconPickerPanel from './IconPickerPanel'

interface IIconItem {
  id: string
  group: string
  label: string
  typeName?: string
}

interface IProps {
  iconNames: readonly string[]
  /** Grouped by type; otherwise one flat grid. */
  isGrouped: boolean
  /** A flat grid's own type, named in its tooltips and found by search. */
  typeName?: string
  /** The project's Type list: it names and orders the groups. */
  types: IHierarchyAccessors
  value: string | null
  onPick: (iconName: string) => void
}

/**
 * One tab's icons: the location's own type's first, then each under the type it belongs to first,
 * or under the type a search found it by. Equipment's icons are one flat grid.
 */
function IconPickerList({
  iconNames,
  isGrouped,
  typeName,
  types,
  value,
  onPick,
}: IProps): React.ReactElement {
  const [query, setQuery] = React.useState('')
  const needle = query.trim().toLocaleLowerCase()

  const items = React.useMemo(() => {
    const matches = (text: string) => text.toLocaleLowerCase().includes(needle)

    return iconNames.flatMap((name): IIconItem[] => {
      const label = iconLabel(name)
      if (!isGrouped) {
        return matches(label) || (!!typeName && matches(typeName))
          ? [{ id: name, group: '', label, typeName }]
          : []
      }

      // Found by one of its types, an icon is listed under that type; otherwise under its home.
      const kinds = kindsOfIcon(name)
      const isHit =
        matches(label) || matches(iconGroupLabel(iconGroupOf(name), types))
      const shownKind = isHit
        ? kinds[0]
        : kinds.find(candidate => matches(typeLabel(candidate, types)))
      if (!isHit && !shownKind) return []

      return [
        {
          id: name,
          group: shownKind ?? iconGroupOf(name),
          label,
          typeName: shownKind && typeLabel(shownKind, types),
        },
      ]
    })
  }, [iconNames, isGrouped, needle, typeName, types])

  const groupConfig = React.useMemo<ICardListGroupConfig<IIconItem>>(
    () => ({
      groupBy: item => item.group,
      getGroupLabel: group => iconGroupLabel(group, types),
      ...iconGroupNesting(types),
    }),
    [types],
  )
  const listState = useCardListCore({
    items,
    groupConfig: isGrouped ? groupConfig : undefined,
  })

  // While searching, every group shows its hits: a collapsed group would hide them.
  const isExpanded = (isCollapsed: boolean) => !isCollapsed || needle !== ''

  return (
    <IconPickerPanel
      query={query}
      onQueryChange={setQuery}
      count={items.length}
      listState={isGrouped ? listState : undefined}
    >
      {isGrouped ? (
        // While searching, a hit under a collapsed parent type still shows.
        (needle ? listState.groups : listState.visibleGroups).map(group => (
          <section
            key={group.key}
            className="tw:border-0 tw:border-b tw:border-solid tw:border-input-soft"
          >
            <GroupHeader>
              <GroupHeaderTrigger
                expanded={isExpanded(group.isCollapsed)}
                onClick={() => listState.toggleGroup(group.key)}
                ariaLabel={group.label}
                leading={<GroupIndent depth={group.depth} />}
                label={group.label}
                count={group.count}
              />
            </GroupHeader>
            {isExpanded(group.isCollapsed) && group.items.length > 0 && (
              <IconPickerGrid>
                {group.items.map(({ id, label, typeName }) => (
                  <IconTile
                    key={id}
                    iconName={id}
                    label={label}
                    typeName={typeName}
                    isSelected={id === value}
                    onPick={onPick}
                  />
                ))}
              </IconPickerGrid>
            )}
          </section>
        ))
      ) : (
        <IconPickerGrid>
          {items.map(({ id, label, typeName: tileTypeName }) => (
            <IconTile
              key={id}
              iconName={id}
              label={label}
              typeName={tileTypeName}
              isSelected={id === value}
              onPick={onPick}
            />
          ))}
        </IconPickerGrid>
      )}
    </IconPickerPanel>
  )
}

export default observer(IconPickerList)
